import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Product {
  key: string;
  name: string;
  specs: string;
}

const products: Product[] = [
  // { key: 'H-100', name: 'H-100', specs: '25.00$|64GB+ RAM|7200rpm|Xeon|2 TFLOPS' },
  { key: 'H-180', name: 'H-180', specs: '95.00$|128GB+ RAM|7200rpm|Xeon|10 TFLOPS' },
  { key: 'H-180 Ti', name: 'H-180 Ti', specs: '175.00$|128GB+ RAM|7200rpm|Xeon|20 TFLOPS' },
  // { key: 'H-790', name: 'H-790', specs: '275.00$|256GB+ RAM|7200rpm|Xeon|30 TFLOPS' },
  { key: 'H-790 Ti', name: 'H-790 Ti', specs: '750.00$|256GB+ RAM|7200rpm|Xeon|80 TFLOPS' },
  // { key: 'HN-990', name: 'HN-990', specs: '925.00$|512GB+ RAM|7200rpm|Xeon|100 TFLOPS' },
  { key: 'HN-990 Ti', name: 'HN-990 Ti', specs: '2250.00$|1TB+ RAM|7200rpm|Xeon|200 TFLOPS' },
  { key: 'HN-x1', name: 'HN-x1', specs: '6350.00$|2TB+ RAM|10000rpm|Xeon|600 TFLOPS' },
  { key: 'HN-x1 Ti', name: 'HN-x1 Ti', specs: '10500.00$|4TB+ RAM|10000rpm|Xeon|1000 TFLOPS' },
  { key: 'X-4080', name: 'X-4080', specs: '25000.00$|8TB+ RAM|10000rpm|Xeon|2500 TFLOPS' },
  { key: 'X-5090', name: 'X-5090', specs: '62000.00$|16TB+ RAM|10000rpm|Xeon|6000 TFLOPS' },
];

const saberMasBtnClass =
  'interactive-scale rounded-[23px] border border-[#BCAAFF] px-8 py-3 text-base text-white shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)]';
const saberMasBtnStyle: React.CSSProperties = {
  background: 'radial-gradient(circle, #060026 0%, #5B22DB 67%, #5100FF 100%)',
};

const ProductModal: React.FC<{
  product: Product;
  onClose: () => void;
}> = ({ product, onClose }) => {
  const totalViews = 4;
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalViews);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalViews) % totalViews);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, goNext, goPrev]);

  const viewSrc = new URL(
    `../assets/products/${product.key}/${currentIndex + 1}.png`,
    import.meta.url,
  ).href;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] max-w-[90vw] flex-col items-center rounded-2xl bg-[#1a1a1a] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
          aria-label="Close"
        >
          ✕
        </button>

        <h3 className="mb-4 text-xl font-bold text-white">{product.name}</h3>

        <div className="relative flex items-center justify-center">
          <button
            onClick={goPrev}
            className="absolute left-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 transition-colors hover:bg-white/25 hover:text-white"
            aria-label="Previous view"
          >
            ‹
          </button>

          <img
            src={viewSrc}
            alt={`${product.name} view ${currentIndex + 1}`}
            className="max-h-[70vh] max-w-[70vw] rounded-xl object-contain"
          />

          <button
            onClick={goNext}
            className="absolute right-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 transition-colors hover:bg-white/25 hover:text-white"
            aria-label="Next view"
          >
            ›
          </button>
        </div>

        <div className="mt-4 flex gap-2">
          {Array.from({ length: totalViews }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-2.5 w-2.5 rounded-full transition-colors ${
                i === currentIndex ? 'bg-[#8A43FF]' : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`View ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Products: React.FC = () => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  return (
    <section className="section-page">
      <div className="max-w-7xl mx-auto">
        <h2 className="mb-12 text-center text-3xl font-bold text-white">{t('products-title')}</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          {products.map((p) => (
            <div
              key={p.key}
              className="group overflow-hidden rounded-2xl bg-[#191919] transition-[box-shadow,ring] duration-300 ease-out hover:ring-1 hover:ring-[#8A43FF]/40"
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-t-2xl">
                <img
                  src={new URL(`../assets/products/${p.key}.png`, import.meta.url).href}
                  alt={p.name}
                  className="h-full w-full rounded-t-2xl object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-t-2xl bg-black/55 opacity-0 transition-opacity duration-300 group-hover:pointer-events-auto group-hover:opacity-100">
                  <button
                    onClick={() => setSelectedProduct(p)}
                    type="button"
                    className={`pointer-events-auto ${saberMasBtnClass}`}
                    style={saberMasBtnStyle}
                  >
                    {t('products-view-more')}
                  </button>
                </div>
              </div>
              <div className="p-5">
                <h3 className="mb-2 text-xl text-white md:text-2xl">{p.name}</h3>
                <p className="text-sm leading-relaxed text-white/80 sm:text-base">{p.specs}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </section>
  );
};
